'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  hourlyRate: 25,
  hoursPerWeek: 40,
  weeksPerYear: 52,
};

const PRESETS = {
  conservative: {
    hourlyRate: 15,
    hoursPerWeek: 35,
    weeksPerYear: 50,
  },
  moderate: {
    hourlyRate: 25,
    hoursPerWeek: 40,
    weeksPerYear: 52,
  },
  aggressive: {
    hourlyRate: 50,
    hoursPerWeek: 45,
    weeksPerYear: 52,
  },
};

export default function CalculatorHourly() {
  const [hourlyRate, setHourlyRate] = useState(INITIAL_STATE.hourlyRate);
  const [hoursPerWeek, setHoursPerWeek] = useState(INITIAL_STATE.hoursPerWeek);
  const [weeksPerYear, setWeeksPerYear] = useState(INITIAL_STATE.weeksPerYear);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const annualSalary = hourlyRate * hoursPerWeek * weeksPerYear;
  const monthlySalary = annualSalary / 12;
  const weeklySalary = hourlyRate * hoursPerWeek;

  const handleReset = () => {
    setHourlyRate(INITIAL_STATE.hourlyRate);
    setHoursPerWeek(INITIAL_STATE.hoursPerWeek);
    setWeeksPerYear(INITIAL_STATE.weeksPerYear);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setHourlyRate(values.hourlyRate);
    setHoursPerWeek(values.hoursPerWeek);
    setWeeksPerYear(values.weeksPerYear);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Hourly Rate</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={hourlyRate}
                onChange={(e) => handleChange(setHourlyRate, Number(e.target.value))}
                step={0.5}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Hours per Week</label>
            <input
              type="number"
              value={hoursPerWeek}
              onChange={(e) => handleChange(setHoursPerWeek, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Weeks per Year</label>
            <input
              type="number"
              value={weeksPerYear}
              onChange={(e) => handleChange(setWeeksPerYear, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Annual Salary</div>
            <div className="text-5xl font-bold">${annualSalary.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Monthly</div>
              <div className="text-xl font-bold text-slate-900">${monthlySalary.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Weekly</div>
              <div className="text-xl font-bold text-slate-900">${weeklySalary.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}